import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { Button } from '..';
import { addGuest, accessTokenExpired } from '../../store/actions';
import { InviteGuestItem } from '../../types/invite.type';
import { Reservation } from 'guest-data-types-ts-lib';
import { ValidationUtils, TranslationUtils, AuthUtils } from '../../utils';
import './InviteGuestModal.scss';
import segmentService from '../../services/segment/segmentService';
import ddLogger from '../../logging/ddLogger';

interface InviteGuestModalProps {
    item: InviteGuestItem;
    show: boolean;
    reservation: Reservation;
    isInvitesFull: boolean;
    handleClose: () => any;
    currentGuestCount: number;
}

interface SecondaryGuest {
    reservationId?: number;
    guestId: number;
    firstName: string;
    lastName: string;
    email: string;
}

export const InviteGuestModal: React.FC<InviteGuestModalProps> = (props) => {
    const dispatch = useDispatch();
    const { item, show, handleClose, reservation, currentGuestCount } = props;

    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [secondaryGuestEdit, setSecondaryGuestEdit] = useState<SecondaryGuest>(item);
    useEffect(() => {
        const guest: SecondaryGuest = { guestId: item.guestId, firstName: item.firstName, lastName: item.lastName, email: item.email };
        setSecondaryGuestEdit(guest);
        setIsSubmitDisabled(!ValidationUtils.isValidGuestForm(secondaryGuestEdit));
    }, [item]);

    useEffect(() => {
        setIsSubmitDisabled(!ValidationUtils.isValidGuestForm(secondaryGuestEdit));
    }, [secondaryGuestEdit]);

    if (!item) {
        return null;
    }

    const handleOnChange = (e: any, property: 'firstName' | 'lastName' | 'email') => {
        const editGuest: SecondaryGuest = { ...secondaryGuestEdit };
        setIsSubmitDisabled(!ValidationUtils.isValidGuestForm(secondaryGuestEdit));
        editGuest[property] = e.target.value;
        setSecondaryGuestEdit(editGuest);
    };

    const handleSubmit = async () => {
        if (AuthUtils.isAccessTokenSessionExpired()) {
            handleClose();
            dispatch(accessTokenExpired(true));
            return;
        }

        setIsSubmitDisabled(true);
        setIsLoading(true);
        const addInvite: any = {
            type: 'reservation-guests',
            attributes: {
                reservation_id: reservation.id,
                first_name: secondaryGuestEdit.firstName,
                last_name: secondaryGuestEdit.lastName,
                email: secondaryGuestEdit.email,
            },
        };
        await dispatch(addGuest(addInvite));
        segmentService.trackEvent('Trip Portal Guest Invites', { action: 'Guest Added', guest_count: currentGuestCount + 1 });
        ddLogger.info(`Guest Added. ReservationId: ${reservation.id}`, { guest_count: currentGuestCount + 1 });
        goToInit();
    };

    const goToInit = () => {
        handleClose();
        setIsSubmitDisabled(false);
        setIsLoading(false);
    };

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header />
            <div className="invite-guest-modal">
                <div className="invite-guest-modal-text">
                    <h4 className={'heading-med-light'}>{TranslationUtils.get('invite_guest_info')}</h4>
                    <p className={'body-regular'}>{TranslationUtils.get('invite_guest_note_modal')}</p>
                </div>
                <div className="row row-5">
                    <div className="col-12 col-md-6">
                        <input
                            id="FirstName"
                            className="body-regular form-control input-padding"
                            value={secondaryGuestEdit.firstName}
                            onChange={(e) => handleOnChange(e, 'firstName')}
                            type="text"
                            placeholder={TranslationUtils.get('first_name')}
                        />
                    </div>
                    <div className="col-12 col-md-6">
                        <input
                            id="LastName"
                            className="body-regular form-control input-padding"
                            value={secondaryGuestEdit.lastName}
                            onChange={(e) => handleOnChange(e, 'lastName')}
                            type="text"
                            placeholder={TranslationUtils.get('last_name')}
                        />
                    </div>
                    <div className="col-12">
                        <input
                            id="Email"
                            className="body-regular form-control input-padding"
                            value={secondaryGuestEdit.email}
                            onChange={(e) => handleOnChange(e, 'email')}
                            type="text"
                            placeholder={TranslationUtils.get('email_address')}
                        />
                    </div>
                </div>
                <div className="invite-guest-modal-buttons">
                    <Button
                        id="button-close-inviteGuestModal"
                        variant="default"
                        label={TranslationUtils.get('cancel')}
                        onClick={handleClose}
                        className={'body-regular'}
                    />
                    <Button
                        id="button-add-inviteGuestModal"
                        variant="primary"
                        isLoading={isLoading}
                        disabled={isSubmitDisabled}
                        label={TranslationUtils.get('invite_guest')}
                        onClick={() => handleSubmit()}
                        className={'body-regular'}
                    />
                </div>
            </div>
        </Modal>
    );
};
